import styled from "@emotion/styled";
import React, {
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from "react";

/**
 * usage:
 * <PanelGroup>
 *   <TabList activeKey='key_id'>
 *      <Tab key='key_0'>Tab 0</Tab>
 *      <Tab key='key_1'>Tab 1</Tab>
 *      <Tab key='key_2'>Tab 2</Tab>
 *   </TabList>
 *   <TabContent>
 *      <TabPane key='key_0'>...</TabPane>
 *      <TabPane key='key_1'>...</TabPane>
 *      <TabPane key='key_2'>...</TabPane>
 *   <TabContent>
 * </Panelroup>
 */

export const PanelGroupStyled = styled.div`
  display: flex;
  flex-direction: column;
  background: var(--builder-panel-group-background);
  border: 1px solid var(--builder-panel-border-outer);
  height: 100%;
`;

export interface IPanelGroupContext {
  activeKey?: string;
  onTabSelect?: (
    activeKey: string | undefined,
    event: React.MouseEvent
  ) => void;
}
export const PanelGroupContext = React.createContext<IPanelGroupContext>({});

export interface IPanelGroupProps {
  /** Expand the Panel, corresponding to the 'Panel' of 'eventkey' */
  activeKey?: string;

  /** Toggles the callback function for the selected tab */
  onSelect?: (
    eventKey: string | undefined,
    event: React.SyntheticEvent
  ) => void;
}

export const PanelGroup: React.FC<PropsWithChildren<IPanelGroupProps>> = ({
  activeKey: propsActiveKey,
  onSelect,
  children,
  ...rest
}) => {
  const [activeKey, setActiveKey] = useState(propsActiveKey);

  const handleSelect = useCallback(
    (activeKey: string | undefined, event: React.MouseEvent) => {
      setActiveKey(activeKey);
      onSelect?.(activeKey, event);
    },
    [onSelect, setActiveKey]
  );

  const contextValue = useMemo<IPanelGroupContext>(
    () => ({ activeKey, onTabSelect: handleSelect }),
    [activeKey]
  );

  return (
    <PanelGroupStyled {...rest}>
      <PanelGroupContext.Provider value={contextValue}>
        {children}
      </PanelGroupContext.Provider>
    </PanelGroupStyled>
  );
};
